/**
 * Quem está logado, e com qual papel.
 *
 * Duas coisas juntas porque nenhuma serve sozinha: o usuário do Supabase Auth
 * diz que alguém entrou, e o `perfil` diz o que essa pessoa é DENTRO da
 * organização. A tela precisa dos dois — sem o segundo ela ofereceria "Trocar
 * papel" para um `leitor`, e a política do banco recusaria depois de o clique
 * já ter parecido possível.
 *
 * O papel **informa** a interface. Quem impede é `0003_rls.sql`.
 */

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import { aoMudarSessao, perfilAtual, sair as sairDaApi, usuarioAtual } from '../servicos/api';
import { mensagemDoErro } from '../servicos/gestao';
import type { Papel, Perfil } from '../tipos';

/**
 * O que a tela precisa saber do usuário — e nada além.
 *
 * Não é o `User` do Supabase: nomear aquele tipo aqui obrigaria este arquivo a
 * escrever `@supabase/supabase-js`, e é justamente essa string que
 * `testes/disciplina.test.ts` proíbe fora de `servicos/api.ts`.
 */
export type Identidade = {
  readonly id: string;
  readonly email: string | null;
};

export type EstadoDaSessao = {
  readonly identidade: Identidade | null;
  readonly perfil: Perfil | null;
  /** Atalho para `perfil?.papel`. `null` quando não há perfil na organização. */
  readonly papel: Papel | null;
  readonly carregando: boolean;
  readonly erro: string | null;
  readonly sair: () => Promise<void>;
};

const ContextoDaSessao = createContext<EstadoDaSessao | null>(null);

/**
 * A única leitura da sessão na prancheta inteira.
 *
 * Menu e tela consomem o mesmo valor. Sem o provedor, cada um montaria seu
 * próprio listener do Supabase Auth e repetiria `perfilAtual()` a cada rota —
 * duas leituras para descobrir a mesma pessoa, além de estados que poderiam
 * divergir durante uma renovação de token.
 */
function useEstadoDaSessao(): EstadoDaSessao {
  const [identidade, definirIdentidade] = useState<Identidade | null>(null);
  const [perfil, definirPerfil] = useState<Perfil | null>(null);
  const [carregando, definirCarregando] = useState(true);
  const [erro, definirErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    definirCarregando(true);
    definirErro(null);
    try {
      const usuario = await usuarioAtual();
      definirIdentidade(usuario ? { id: usuario.id, email: usuario.email ?? null } : null);
      // Sem usuário não há o que perguntar ao `perfil`: a política de leitura
      // dele depende de `auth.uid()`, e a chamada voltaria vazia de qualquer
      // jeito — com uma ida ao banco a mais em cada carregamento da tela de
      // entrar.
      definirPerfil(usuario ? await perfilAtual() : null);
    } catch (e: unknown) {
      definirErro(mensagemDoErro(e, 'ler sua sessão'));
      definirIdentidade(null);
      definirPerfil(null);
    } finally {
      definirCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
    // Entrar, sair e a renovação do token chegam por aqui. Sem isto, entrar
    // deixaria a tela na porta até alguém apertar F5.
    return aoMudarSessao(() => {
      void carregar();
    });
  }, [carregar]);

  const sair = useCallback(async () => {
    await sairDaApi();
    definirIdentidade(null);
    definirPerfil(null);
  }, []);

  return {
    identidade,
    perfil,
    papel: perfil?.papel ?? null,
    carregando,
    erro,
    sair,
  };
}

export function ProvedorDaSessao({ children }: { readonly children: ReactNode }) {
  const estado = useEstadoDaSessao();
  return createElement(ContextoDaSessao.Provider, { value: estado }, children);
}

export function useSessao(): EstadoDaSessao {
  const estado = useContext(ContextoDaSessao);
  if (!estado) {
    throw new Error('useSessao precisa estar dentro de ProvedorDaSessao');
  }
  return estado;
}
